import React, { useState, useEffect } from 'react';
import { FaBed, FaBath, FaHome, FaTimesCircle } from 'react-icons/fa';

/**
 * OffMarketModal
 * Props:
 *  - listing: the card/row clicked (we detect off-market via !listing.formattedAddress)
 *  - details: object returned by your scraper or by the fetch wrapper:
 *      Normal success: { address, image, sqft, beds, baths, realtorImage, wholesellerName, wholesaleCompany, description }
 *      Sold/unavailable: { sold: true }
 *      Timeout: { timeout: true, onRetry?: fn }
 *      Error: { error: 'message', onRetry?: fn }
 *  - onClose: () => void
 *  - onRetry (optional): () => void   // parent can pass this; otherwise we look for details.onRetry
 */
export default function OffMarketModal({ listing, details, onClose, onRetry }) {
  // Is this an off-market item?
  const isOffMarket = !listing?.formattedAddress;

  // Consider "loaded" if we have any of: address, sold, error, timeout.
  const isLoading =
    isOffMarket &&
    !(
      details &&
      (details.address || details.sold || details.error || details.timeout)
    );

  // Build an array of up to 10 image URLs by incrementing the number in details.image
  let imagesList = [];
  if (details?.image) {
    const match = details.image.match(/^(.+\/)(\d+)(\.jpg)$/);
    if (match) {
      const prefix = match[1];
      const baseNumber = parseInt(match[2], 10);
      const suffix = match[3];
      imagesList = Array.from({ length: 10 }, (_, i) => `${prefix}${baseNumber + i}${suffix}`);
    } else {
      imagesList = [details.image];
    }
  }

  // Carousel state
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  // “Alive” loading tips (purely cosmetic)
  const tips = [
    'Checking availability…',
    'Pulling photos…',
    'Collecting wholeseller info…',
  ];
  const [tipIdx, setTipIdx] = useState(0);
  useEffect(() => {
    if (!isLoading) return;
    const id = setInterval(() => setTipIdx(i => (i + 1) % tips.length), 1500);
    return () => clearInterval(id);
  }, [isLoading]);

  // Choose a smaller modal style when loading; otherwise, use the full-size modal
  const modalContent = isLoading
    ? {
        background: '#fff',
        borderRadius: '15px',
        width: '320px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        padding: '20px',
        textAlign: 'center',
        position: 'relative',
        animation: 'fadeIn 0.3s ease-out',
      }
    : {
        background: '#fff',
        borderRadius: '15px',
        width: '90%',
        maxWidth: '600px',
        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
        overflow: 'hidden',
        position: 'relative',
        animation: 'fadeIn 0.3s ease-out',
      };

  // Common inline styles
  const modalOverlay = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100vh',
    backgroundColor: 'rgba(0,0,0,0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  };

  const closeButton = {
    position: 'absolute',
    top: '10px',
    right: '10px',
    background: 'transparent',
    border: 'none',
    fontSize: '28px',
    lineHeight: 1,
    cursor: 'pointer',
    color: '#fff',
    zIndex: 10,
  };

  // Styles used when details are loaded
  const headerStyle = {
    width: '100%',
    height: '250px',
    position: 'relative',
    overflow: 'hidden',
    backgroundColor: '#000',
  };

  const propertyImageStyle = {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    display: 'block',
  };

  const placeholderImageStyle = {
    width: '100%',
    height: '100%',
    backgroundColor: '#ddd',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '60px',
    color: '#888',
  };

  // Carousel arrows
  const arrowButton = {
    position: 'absolute',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'rgba(255,255,255,0.7)',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    padding: '8px',
    borderRadius: '4px',
    zIndex: 5,
  };
  const arrowLeftStyle = { ...arrowButton, left: '10px' };
  const arrowRightStyle = { ...arrowButton, right: '10px' };

  const modalBody = { padding: '20px' };

  const modalText = {
    margin: '10px 0',
    fontSize: '16px',
    color: '#555',
  };

  const iconStyle = { marginRight: '5px', color: '#007aff' };

  const contactSection = {
    display: 'flex',
    alignItems: 'center',
    margin: '15px 0',
  };

  const realtorImageStyle = {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    marginRight: '15px',
    objectFit: 'cover',
    border: '2px solid #007aff',
  };

  const descriptionStyle = { marginTop: '15px', lineHeight: 1.5 };

  const modalFooter = {
    padding: '10px 20px',
    textAlign: 'right',
    borderTop: '1px solid #eee',
  };

  const modalCloseButton = {
    backgroundColor: '#007aff',
    color: '#fff',
    border: 'none',
    borderRadius: '5px',
    padding: '10px 20px',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'background-color 0.2s ease',
  };

  // Buttons for secondary blocks
  const buttonPrimary = {
    backgroundColor: '#007aff',
    color: '#fff',
    border: 'none',
    borderRadius: 6,
    padding: '8px 14px',
    cursor: 'pointer',
  };
  const buttonSecondary = {
    backgroundColor: '#fff',
    color: '#007aff',
    border: '1px solid #007aff',
    borderRadius: 6,
    padding: '8px 14px',
    cursor: 'pointer',
  };

  // Reusable blocks
  function LoadingBlock() {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontSize: 50, color: '#007aff', marginBottom: 10 }}>
          <FaHome />
        </div>
        <div style={{ width: 240, height: 6, background: '#eee', borderRadius: 4, overflow: 'hidden', marginBottom: 10 }}>
          <div
            style={{
              width: '40%',
              height: '100%',
              background: '#007aff',
              animation: 'indeterminate 1.2s ease-in-out infinite',
            }}
          />
        </div>
        <p style={{ fontSize: 16, color: '#555', margin: 0 }}>Loading details…</p>
        <p style={{ fontSize: 13, color: '#888', marginTop: 6 }}>{tips[tipIdx]}</p>
      </div>
    );
  }

  function SoldBlock() {
    return (
      <div style={{ textAlign: 'center', padding: '40px 0', color: '#2c3e50', fontSize: 18, fontWeight: 'bold' }}>
        This off-market deal looks <span style={{ color: '#e74c3c' }}>sold / unavailable</span> right now.
      </div>
    );
  }

  function TimeoutBlock() {
    const retry = onRetry || details?.onRetry;
    return (
      <div style={{ textAlign: 'center', padding: '32px 0' }}>
        <p style={{ fontSize: 16, color: '#555', marginBottom: 12 }}>
          Still trying to fetch this listing, but it’s taking too long.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          {retry && (
            <button style={buttonSecondary} onClick={retry}>
              Retry
            </button>
          )}
          <button style={buttonPrimary} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  function ErrorBlock() {
    const retry = onRetry || details?.onRetry;
    const msg = details?.error || 'Unknown error';
    return (
      <div style={{ textAlign: 'center', padding: '32px 0' }}>
        <p style={{ fontSize: 16, color: '#555', marginBottom: 8 }}>Couldn’t load details for this property.</p>
        <p style={{ fontSize: 13, color: '#999', marginBottom: 12 }}>{String(msg)}</p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          {retry && (
            <button style={buttonSecondary} onClick={retry}>
              Retry
            </button>
          )}
          <button style={buttonPrimary} onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={(e) => e.stopPropagation()}>
        {/* Close button only in full-size (non-loading) mode */}
        {!isLoading && (
          <button style={closeButton} onClick={onClose} aria-label="Close">
            <FaTimesCircle />
          </button>
        )}

        {isLoading ? (
          <LoadingBlock />
        ) : details?.sold ? (
          <SoldBlock />
        ) : details?.timeout ? (
          <TimeoutBlock />
        ) : details?.error ? (
          <ErrorBlock />
        ) : (
          // Full details state
          <>
            <div style={headerStyle}>
              {imagesList.length > 0 ? (
                <>
                  <img
                    src={imagesList[currentImageIndex]}
                    alt={`Property ${currentImageIndex + 1}`}
                    style={propertyImageStyle}
                  />
                  <button
                    style={arrowLeftStyle}
                    onClick={() => setCurrentImageIndex((idx) => Math.max(idx - 1, 0))}
                    disabled={currentImageIndex === 0}
                    aria-label="Previous photo"
                  >
                    ‹
                  </button>
                  <button
                    style={arrowRightStyle}
                    onClick={() =>
                      setCurrentImageIndex((idx) => Math.min(idx + 1, imagesList.length - 1)
                      )
                    }
                    disabled={currentImageIndex === imagesList.length - 1}
                    aria-label="Next photo"
                  >
                    ›
                  </button>
                </>
              ) : (
                <div style={placeholderImageStyle}>
                  <FaHome />
                </div>
              )}
            </div>

            <div style={modalBody}>
              {/* Address */}
              {details?.address && (
                <p style={{ ...modalText, textAlign: 'center' }}>{details.address}</p>
              )}

              {/* SQFT / Beds / Baths */}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  margin: '10px 0',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center' }}>
                  <span style={modalText}>
                    <strong>SQFT:</strong> {details?.sqft ?? '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center' }}>
                  <FaBed style={iconStyle} />
                  <span style={modalText}>
                    <strong>Beds:</strong> {details?.beds ?? '—'}
                  </span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', flex: 1, justifyContent: 'center' }}>
                  <FaBath style={iconStyle} />
                  <span style={modalText}>
                    <strong>Baths:</strong> {details?.baths ?? '—'}
                  </span>
                </div>
              </div>

              {/* Contact / wholeseller */}
              <div style={contactSection}>
                {details?.realtorImage && (
                  <img src={details.realtorImage} alt="Realtor" style={realtorImageStyle} />
                )}
                <div>
                  {details?.wholesellerName && (
                    <p style={modalText}>
                      <strong>Wholeseller:</strong> {details.wholesellerName}
                    </p>
                  )}
                  {details?.wholesaleCompany && (
                    <p style={modalText}>
                      <strong>Wholesale Company:</strong> {details.wholesaleCompany}
                    </p>
                  )}
                </div>
              </div>

              {/* Description */}
              {details?.description && (
                <p style={{ ...modalText, ...descriptionStyle }}>
                  <strong>Description:</strong> {details.description}
                </p>
              )}
            </div>

            <div style={modalFooter}>
              <button style={modalCloseButton} onClick={onClose}>
                Close
              </button>
            </div>
          </>
        )}
      </div>

      {/* Animations */}
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes fadeIn {
            from { opacity: 0; transform: scale(0.95); }
            to { opacity: 1; transform: scale(1); }
          }
          @keyframes indeterminate {
            0%   { transform: translateX(-100%); }
            50%  { transform: translateX(50%); }
            100% { transform: translateX(200%); }
          }
        `}
      </style>
    </div>
  );
}